import type { ComponentType } from "react";
import {
  ShieldIcon, BookIcon, TransportIcon, MessageSquareIcon,
  MealIcon, SparkleIcon, DocumentIcon, ReceiptIcon,
} from "@/components/ui/TriIcons";

const GOLD = "#C9A45C";
const GREEN = "#145C4A";
const DARK_GREEN = "#0D3B32";

export type ProductDetail = {
  id: string;
  name: string;
  shortName: string;
  category: string;
  tagline: string;
  description: string;
  icon: ComponentType<{ accent?: string; className?: string; style?: React.CSSProperties }>;
  color: string;
  gradient: string;
  capabilities: string[];
  powers?: string[];
  supports?: string[];
  knows?: string[];
  principles?: string[];
  aiCanAnswer?: string[];
  metric: { value: string; label: string };
  unique: string;
  stats: { value: string; label: string }[];
  visuals: {
    type: "identity" | "learn" | "transit" | "connect" | "campus" | "intelligence" | "government" | "pay";
  };
};

export const products: ProductDetail[] = [
  {
    id: "identity",
    name: "BRECCS Records",
    shortName: "Records",
    category: "Foundation",
    tagline: "Digital identity and records for every learner, family, and staff member.",
    description:
      "BRECCS Records is the identity and records layer for Zimbabwean schools. Every learner, guardian, staff member, supplier, and institution is assigned a persistent digital identity with governed access, consent controls, and full audit history. It manages admissions, enrolment, transfers, guardian relationships, documents, and permissions across every campus in a school group.",
    icon: ShieldIcon,
    color: GREEN,
    gradient: "linear-gradient(135deg, #145C4A, #0D3B32)",
    capabilities: [
      "Learner Identity Management",
      "Guardian & Family Identity",
      "Staff & Supplier Identity",
      "Digital ID Cards",
      "QR & NFC Credentials",
      "Role & Permission Management",
      "Household Relationships",
      "Multi-school Enrollment",
      "Document Vault",
      "Consent & Privacy Controls",
      "Identity Verification",
      "Audit History",
    ],
    powers: [
      "Admissions",
      "Attendance",
      "Examinations",
      "Payments",
      "Transport",
      "Canteen",
      "Boarding",
      "Healthcare",
      "Communication",
      "Institutional reporting",
    ],
    metric: { value: "100%", label: "Verified enrollment" },
    unique:
      "One governed identity works across every campus system — money, records, canteen, transport, and boarding.",
    stats: [
      { value: "1,842", label: "Learners tracked" },
      { value: "12+", label: "Record types" },
      { value: "100%", label: "SSO coverage" },
      { value: "0", label: "Duplicate records" },
    ],
    visuals: { type: "identity" },
  },
  {
    id: "learn",
    name: "BRECCS Learn",
    shortName: "Learn",
    category: "Learning",
    tagline: "Zimbabwe education: ECD through A-Level, ZIMSEC and Cambridge pathways.",
    description:
      "BRECCS Learn manages curriculum delivery, assessments, and academic progress for Zimbabwean schools. It supports ECD, primary, secondary, and A-Level/Sixth Form structures with configurable subjects, continuous assessment, ZIMSEC and Cambridge pathways, teacher markbooks, report cards, learner portfolios, and parent visibility.",
    icon: BookIcon,
    color: GOLD,
    gradient: "linear-gradient(135deg, #C9A45C, #D4B06A)",
    capabilities: [
      "Curriculum Management",
      "Lesson Planning",
      "Timetabling",
      "Digital Classrooms",
      "Assignments",
      "Assessment & Exams",
      "Continuous Assessment",
      "Grading & Report Cards",
      "ZIMSEC Pathway",
      "Cambridge Pathway",
      "Teacher Markbooks",
      "Learner Portfolios",
      "Intervention Plans",
      "Parent Progress Portal",
      "Attendance Integration",
    ],
    supports: [
      "ECD, primary, secondary, A-Level",
      "ZIMSEC and Cambridge",
      "Continuous assessment",
      "Configurable curriculum rules",
    ],
    metric: { value: "1,842", label: "Active learners" },
    unique:
      "Learning records feed directly into the family experience — no separate app, no separate login.",
    stats: [
      { value: "1,842", label: "Active learners" },
      { value: "340+", label: "Curriculum subjects" },
      { value: "94%", label: "Assignment completion" },
      { value: "2", label: "Examination pathways" },
    ],
    visuals: { type: "learn" },
  },
  {
    id: "transit",
    name: "BRECCS Transit",
    shortName: "Transit",
    category: "Transport",
    tagline: "Every bus. Every route. Every child. Verified handover.",
    description:
      "BRECCS Transit manages school transportation with real-time visibility into fleets, drivers, routes, and learner journeys. It supports QR/NFC/PIN boarding, offline boarding queues, verified pick-up and drop-off with authorised adult handover, late and missed boarding alerts, route deviation detection, and parent status notifications.",
    icon: TransportIcon,
    color: GREEN,
    gradient: "linear-gradient(135deg, #145C4A, #3A8A6A)",
    capabilities: [
      "Fleet Management",
      "Route Planning",
      "GPS Tracking",
      "Driver & Attendant Records",
      "Learner Boarding (QR/NFC/PIN)",
      "Offline Boarding Queue",
      "Verified Pick-up & Drop-off",
      "Authorised Adult Handover",
      "Late & Missed Boarding Alerts",
      "Route Deviation Detection",
      "Journey-Time Alerts",
      "Incident Reporting",
      "Fuel & Trip Logs",
      "Parent Status Notifications",
      "Transport Fees & Reconciliation",
      "Emergency Contacts",
    ],
    knows: [
      "who boarded",
      "who missed the bus",
      "current location",
      "estimated arrival",
      "delays",
      "route deviations",
      "verified handover",
    ],
    metric: { value: "18", label: "Active vehicles" },
    unique:
      "Verified handover confirms the authorised adult at drop-off — not just that the bus arrived.",
    stats: [
      { value: "18", label: "Vehicles tracked" },
      { value: "94.2%", label: "On-time arrival" },
      { value: "100%", label: "Verified handover" },
      { value: "0", label: "Unresolved incidents" },
    ],
    visuals: { type: "transit" },
  },
  {
    id: "connect",
    name: "BRECCS Connect",
    shortName: "Connect",
    category: "Communication",
    tagline: "Every conversation. One trusted channel. Full audit.",
    description:
      "BRECCS Connect unifies school communication into a single, secure platform with SMS, email, approved WhatsApp, in-app notifications, announcements, targeted class/house/route/campus messages, emergency broadcasts, acknowledgement status, and full communication audit. It also manages SIS/ERP, accounting, payment, and messaging integrations.",
    icon: MessageSquareIcon,
    color: GOLD,
    gradient: "linear-gradient(135deg, #C9A45C, #D4B06A)",
    capabilities: [
      "Parent Messaging",
      "Broadcast Announcements",
      "Emergency Notifications",
      "SMS",
      "Approved WhatsApp",
      "Email",
      "Push Notifications",
      "Targeted Class/House/Route Messages",
      "Acknowledgement Status",
      "Communication Audit",
      "SIS/ERP Integration",
      "Accounting Integration",
      "Payment Integration",
      "API & Webhooks",
      "CSV Import/Export",
      "Sync Status & Conflict Queue",
    ],
    metric: { value: "99.2%", label: "Delivery rate" },
    unique:
      "Every message is auditable — see who received it, when, and whether they acknowledged it.",
    stats: [
      { value: "99.2%", label: "Delivery rate" },
      { value: "6+", label: "Channels unified" },
      { value: "100%", label: "Audit coverage" },
      { value: "2.4K", label: "Messages/month" },
    ],
    visuals: { type: "connect" },
  },
  {
    id: "campus",
    name: "BRECCS Nourish",
    shortName: "Nourish",
    category: "School Feeding",
    tagline: "The school food operating system. From forecast to meal served.",
    description:
      "BRECCS Nourish is not a meal register — it is the school food operating system. It forecasts meals from attendance and boarding data, plans menus by age band and dietary rules, calculates ingredient requirements, monitors stock cover and stockout risk, manages kitchen production plans, captures meal service in low-connectivity conditions, tracks waste and cost per learner, and feeds procurement intelligence.",
    icon: MealIcon,
    color: GREEN,
    gradient: "linear-gradient(135deg, #145C4A, #4A9A7A)",
    capabilities: [
      "Attendance & Boarding Forecast",
      "Meal Forecasts",
      "Weekly Menu Planner",
      "Age-Band & Dietary Rules",
      "Allergy Management",
      "Ingredient Requirement Calculation",
      "Stock Cover & Stockout Prediction",
      "Kitchen Production Plans",
      "Batch Quantities & Tasks",
      "Serving Windows",
      "Low-Connectivity Meal Capture",
      "Waste Tracking (Spoilage, Overproduction, Plate Waste)",
      "Cost per Meal & Learner",
      "Local Farmer & Producer Onboarding",
      "Feeding & Nutrition Dashboards",
    ],
    metric: { value: "96.7%", label: "Meal fulfilment" },
    unique:
      "BRECCS knows cooking oil will reach 3.2 days of cover and recommends placing an order today — before the school runs out.",
    stats: [
      { value: "96.7%", label: "Meal fulfilment" },
      { value: "6 days", label: "Maize cover" },
      { value: "420L", label: "Oil requirement" },
      { value: "USD 0.84", label: "Cost per meal" },
    ],
    visuals: { type: "campus" },
  },
  {
    id: "intelligence",
    name: "BRECCS Intelligence",
    shortName: "Intelligence",
    category: "Intelligence",
    tagline: "Governed decision support. Evidence behind every recommendation.",
    description:
      "BRECCS Intelligence transforms operational data into explainable forecasts, recommendations, and benchmarks. It predicts collections, stockout risk, demand, delivery risk, meal quantities, route delays, and attendance anomalies. Every insight shows data sources, timestamp, confidence, affected population, and suggested next action. Includes authorised natural-language questions with evidence lineage.",
    icon: SparkleIcon,
    color: GOLD,
    gradient: "linear-gradient(135deg, #C9A45C, #D4B06A)",
    capabilities: [
      "Collections Forecast",
      "Payment-Match Confidence",
      "Arrears Risk",
      "Stockout Prediction",
      "Demand Forecast",
      "Supplier Recommendation",
      "Delivery Risk",
      "Meal Quantity & Waste Forecast",
      "Route Delay & Attendance Anomaly Detection",
      "Incident Escalation",
      "Group Benchmarking",
      "What-If Budgets",
      "Authorised Natural-Language Questions",
      "Evidence & Lineage for Every Insight",
    ],
    aiCanAnswer: [
      "Which campuses have the highest stockout risk?",
      "Why are collections behind forecast?",
      "Which suppliers underperformed this month?",
      "How much cooking oil will we need next week?",
      "Which transport routes have repeated delays?",
    ],
    metric: { value: "20", label: "Active exceptions" },
    unique:
      "Every alert shows evidence, impact, recommended action, owner, and deadline — not just a number.",
    stats: [
      { value: "20", label: "Active exceptions" },
      { value: "87.4%", label: "Collection rate" },
      { value: "94.2%", label: "Attendance" },
      { value: "96.7%", label: "Meal fulfilment" },
    ],
    visuals: { type: "intelligence" },
  },
  {
    id: "government",
    name: "BRECCS Institutional",
    shortName: "Institutional",
    category: "Compliance",
    tagline: "Authorised aggregate intelligence for institutions.",
    description:
      "BRECCS Institutional provides authorised aggregate dashboards for ministries, districts, and authorised institutions. It shows school registry, enrolment, staffing, infrastructure, attendance, performance, feeding, transport, procurement, and service indicators — with geographic intelligence, controlled exports, disclosure thresholds, and role-based access. It does not expose individual learner records.",
    icon: DocumentIcon,
    color: GREEN,
    gradient: "linear-gradient(135deg, #145C4A, #0D3B32)",
    capabilities: [
      "School & Campus Registry",
      "Aggregate Enrollment Indicators",
      "Staffing Indicators",
      "Infrastructure Indicators",
      "Attendance & Performance Indicators",
      "Feeding & Transport Indicators",
      "Procurement Indicators",
      "Geographic Intelligence",
      "Controlled Dashboards & Exports",
      "Disclosure Thresholds",
      "Role-Based Access",
      "Data Aggregation Controls",
    ],
    metric: { value: "12", label: "Schools registered" },
    unique:
      "Schools remain custodians of their data; BRECCS is the governed technology layer — not a national learner database.",
    stats: [
      { value: "12", label: "Schools registered" },
      { value: "1,842", label: "Aggregate learners" },
      { value: "100%", label: "Aggregation compliance" },
      { value: "0", label: "Individual records exposed" },
    ],
    visuals: { type: "government" },
  },
  {
    id: "pay",
    name: "BRECCS Money",
    shortName: "Money",
    category: "Finance",
    tagline: "Paid. Allocated. Reconciled. Receipted.",
    description:
      "BRECCS Money is the financial control centre for Zimbabwean school groups. It manages fee structures, invoices, statements, discounts, bursaries, sponsors, instalments, and payment plans. It supports bank, Zimswitch, mobile money, card, USSD, agent, and cash deposit channels with payment matching, unmatched-payment queues, reconciliation, arrears workflows, and currency-aware reporting for USD and ZiG.",
    icon: ReceiptIcon,
    color: GOLD,
    gradient: "linear-gradient(135deg, #C9A45C, #D4B06A)",
    capabilities: [
      "Fee Structures & Invoices",
      "Statements & Discounts",
      "Bursaries & Sponsors",
      "Instalment & Payment Plans",
      "Multi-Child Obligations",
      "Bank Transfer Integration",
      "Zimswitch Payment Integration",
      "Mobile Money Integration",
      "Card Payments",
      "USSD Payments",
      "Agent & Cash Deposit",
      "Payment Matching & Allocation",
      "Unmatched-Payment Queue",
      "Receipts, Reversals, Refunds",
      "Arrears Workflow",
      "Collections Forecast",
      "Budgets & Approvals",
      "Accounting Export",
      "Audit Log",
      "USD & ZiG Currency Support",
    ],
    principles: [
      "Payment collection is an integration requirement, not the product category.",
      "Win on matching, allocation, reconciliation, and cash-flow control.",
      "Currency-aware invoices, receipts, settlements, and reporting for USD and ZiG.",
      "School sees: paid, allocated, reconciled, receipted.",
      "Do not make payment commissions the core model.",
    ],
    metric: { value: "USD 1.82M", label: "Expected this term" },
    unique:
      "The unmatched-payment queue shows 14 payments that cannot be confidently allocated — with suggested matches and confidence scores.",
    stats: [
      { value: "USD 1.82M", label: "Expected this term" },
      { value: "87.4%", label: "Collected" },
      { value: "14", label: "Unmatched payments" },
      { value: "99.9%", label: "Reconciliation rate" },
    ],
    visuals: { type: "pay" },
  },
];

export function getProductById(id: string): ProductDetail | undefined {
  return products.find((p) => p.id === id);
}
